// ============================================
// Export Button
// CSV download + Google Sheets export
// ============================================

import { useCallback } from 'react';
import { Download, Sheet } from 'lucide-react';
import { toCSV, downloadCSV, openInGoogleSheets } from '../../../utils/csvExport';
import type { Order } from '../../../types/order';
import type { DateRange } from './useFinancialData';

interface ExportButtonProps {
  orders: Order[];
  dateRange: DateRange;
}

const CSV_COLUMNS = [
  { key: 'id', label: 'Order ID' },
  { key: 'status', label: 'Status' },
  { key: 'bundleName', label: 'Bundle' },
  { key: 'totalFormatted', label: 'Total ($)' },
  { key: 'requiresProvider', label: 'Provider Required' },
  { key: 'customerEmail', label: 'Customer Email' },
  { key: 'createdAt', label: 'Created' },
  { key: 'paidAt', label: 'Paid' },
  { key: 'shippedAt', label: 'Shipped' },
  { key: 'deliveredAt', label: 'Delivered' },
];

function prepareRows(orders: Order[]) {
  return orders.map((o) => ({
    id: o.id,
    status: o.status,
    bundleName: o.bundleName || '',
    totalFormatted: (o.totalInCents / 100).toFixed(2),
    requiresProvider: o.requiresProvider ? 'Yes' : 'No',
    customerEmail: o.customerEmail,
    createdAt: o.createdAt,
    paidAt: o.paidAt || '',
    shippedAt: o.shippedAt || '',
    deliveredAt: o.deliveredAt || '',
  }));
}

export function ExportButton({ orders, dateRange }: ExportButtonProps) {
  const filename = `hemologix-financial-${dateRange.from}-to-${dateRange.to}.csv`;

  const handleCSV = useCallback(() => {
    const rows = prepareRows(orders);
    const csv = toCSV(rows, CSV_COLUMNS);
    downloadCSV(csv, filename);
  }, [orders, filename]);

  const handleSheets = useCallback(() => {
    const rows = prepareRows(orders);
    const csv = toCSV(rows, CSV_COLUMNS);
    openInGoogleSheets(csv, `Hemologix Financial ${dateRange.from} to ${dateRange.to}`);
  }, [orders, dateRange]);

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={handleCSV}
        disabled={orders.length === 0}
        className="px-3 py-2 bg-bg-elevated rounded-lg text-text-secondary hover:text-text-primary text-sm flex items-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        title="Download CSV"
      >
        <Download size={16} />
        CSV
      </button>
      <button
        onClick={handleSheets}
        disabled={orders.length === 0}
        className="px-3 py-2 bg-bg-elevated rounded-lg text-text-secondary hover:text-text-primary text-sm flex items-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        title="Open in Google Sheets"
      >
        <Sheet size={16} />
        Sheets
      </button>
    </div>
  );
}
